// Claim-by-claim lint for a decoded ID-JAG (Identity Assertion Authorization
// Grant). Pure functions: each claim gets a pass/warn/fail verdict with a
// spec citation, so the inspector can explain WHY a claim matters rather than
// only flagging mismatches against the configured flow.

export type IdJagLintStatus = "pass" | "warn" | "fail";

export interface IdJagLintCitation {
  spec: string;
  section: string;
}

export interface IdJagLintVerdict {
  id:
    | "typ"
    | "iss"
    | "sub"
    | "aud"
    | "resource"
    | "client_id"
    | "jti"
    | "iat"
    | "exp"
    | "subject_resolution"
    | "sub_id";
  claim: string;
  status: IdJagLintStatus;
  detail: string;
  citation: IdJagLintCitation;
  actual?: string;
}

/**
 * Expected values from the configured flow. All optional — when absent, the
 * lint degrades to presence/format checks instead of exact-match checks.
 */
export interface IdJagLintContext {
  expectedIssuer?: string;
  expectedAudience?: string;
  expectedResource?: string;
  expectedClientId?: string;
  /** Seconds-since-epoch "now"; injectable for tests. */
  nowSeconds?: number;
}

const ID_JAG_TYP = "oauth-id-jag+jwt";

// Lifetimes beyond this draw a warning: the ID-JAG is a single-use,
// immediately-redeemed artifact, so a long exp only widens the replay window.
const LONG_LIVED_THRESHOLD_S = 15 * 60;

const CITATIONS = {
  typ: { spec: "ID-JAG draft", section: "JWT typ header" },
  typBcp: { spec: "RFC 8725", section: "§3.11 explicit typing" },
  iss: { spec: "RFC 7523", section: "§3 (iss required)" },
  sub: { spec: "RFC 7523", section: "§3 (sub identifies the principal)" },
  aud: {
    spec: "RFC 7523",
    section: "§3 (aud must identify the authorization server)",
  },
  resource: { spec: "RFC 8707 / ID-JAG draft", section: "resource indicator" },
  clientId: { spec: "ID-JAG draft", section: "client_id binding" },
  jti: { spec: "RFC 7519", section: "§4.1.7 (jti replay prevention)" },
  iat: { spec: "ID-JAG draft", section: "iat (required claim)" },
  exp: { spec: "RFC 7523", section: "§3 (exp required; reject expired)" },
  subjectResolution: {
    spec: "ID-JAG draft",
    section: "subject resolution (email / aud_sub RECOMMENDED)",
  },
  subId: {
    spec: "ID-JAG draft",
    section: "§3.2.2 sub_id (structured subject identifier)",
  },
} as const satisfies Record<string, IdJagLintCitation>;

// Tolerated forward clock skew before an iat "issued in the future" warning.
const IAT_SKEW_S = 60;

function show(value: unknown): string {
  if (value === undefined) return "(absent)";
  if (value === null) return "null";
  if (typeof value === "string") return value;
  return JSON.stringify(value);
}

function isNonEmptyString(value: unknown): value is string {
  return typeof value === "string" && value.trim().length > 0;
}

// The maximum time value a JS Date can represent (±100,000,000 days from the
// epoch, in ms). Beyond it, `new Date(ms).toISOString()` throws a RangeError.
const MAX_DATE_MS = 8.64e15;

// Format an epoch-seconds claim as ISO-8601, falling back to the raw numeric
// value when it's outside the representable Date range. A malformed JWT can
// carry an out-of-range iat/exp, and the lint must render a verdict rather
// than crash the whole inspector.
function formatEpochSeconds(seconds: number): string {
  const ms = seconds * 1000;
  if (!Number.isFinite(ms) || Math.abs(ms) > MAX_DATE_MS) {
    return String(seconds);
  }
  return new Date(ms).toISOString();
}

export function lintIdJag(
  header: Record<string, unknown> | null,
  payload: Record<string, unknown> | null,
  context: IdJagLintContext = {}
): IdJagLintVerdict[] {
  const h = header ?? {};
  const p = payload ?? {};
  const now = context.nowSeconds ?? Math.floor(Date.now() / 1000);
  const verdicts: IdJagLintVerdict[] = [];

  // typ header — explicit typing prevents this JWT from being replayed where
  // a generic JWT (or an ID token) is accepted.
  verdicts.push(
    h.typ === ID_JAG_TYP
      ? {
          id: "typ",
          claim: "typ header",
          status: "pass",
          detail: `Explicitly typed as ${ID_JAG_TYP}, so the assertion can't be confused with an ID token or generic JWT.`,
          citation: CITATIONS.typ,
          actual: show(h.typ),
        }
      : {
          id: "typ",
          claim: "typ header",
          status: "fail",
          detail: `Expected ${ID_JAG_TYP}. Without explicit typing, an authorization server may accept this token in contexts it was never meant for (token-confusion).`,
          citation: h.typ === "JWT" ? CITATIONS.typBcp : CITATIONS.typ,
          actual: show(h.typ),
        }
  );

  // iss
  if (!isNonEmptyString(p.iss)) {
    verdicts.push({
      id: "iss",
      claim: "iss",
      status: "fail",
      detail:
        "Missing issuer. The authorization server can't establish which identity provider signed the assertion.",
      citation: CITATIONS.iss,
      actual: show(p.iss),
    });
  } else if (context.expectedIssuer && p.iss !== context.expectedIssuer) {
    verdicts.push({
      id: "iss",
      claim: "iss",
      status: "fail",
      detail: `Issuer does not match the identity provider configured for this flow (${context.expectedIssuer}). The authorization server will reject the assertion as untrusted.`,
      citation: CITATIONS.iss,
      actual: p.iss,
    });
  } else {
    verdicts.push({
      id: "iss",
      claim: "iss",
      status: "pass",
      detail: "Issuer present and matches the identity provider.",
      citation: CITATIONS.iss,
      actual: p.iss,
    });
  }

  // sub
  verdicts.push(
    isNonEmptyString(p.sub)
      ? {
          id: "sub",
          claim: "sub",
          status: "pass",
          detail: "Subject identifies the user the grant was issued for.",
          citation: CITATIONS.sub,
          actual: p.sub,
        }
      : {
          id: "sub",
          claim: "sub",
          status: "fail",
          detail:
            "Missing subject. The authorization server can't tell which user this grant represents.",
          citation: CITATIONS.sub,
          actual: show(p.sub),
        }
  );

  // aud — exact match against the authorization server's issuer identifier.
  if (!isNonEmptyString(p.aud)) {
    verdicts.push({
      id: "aud",
      claim: "aud",
      status: "fail",
      detail: Array.isArray(p.aud)
        ? "Audience is an array. The ID-JAG audience must be the single issuer identifier of the authorization server it will be redeemed at."
        : "Missing audience. The assertion must name the authorization server it will be redeemed at.",
      citation: CITATIONS.aud,
      actual: show(p.aud),
    });
  } else if (context.expectedAudience && p.aud !== context.expectedAudience) {
    verdicts.push({
      id: "aud",
      claim: "aud",
      status: "fail",
      detail: `Audience must exactly match the authorization server issuer (${context.expectedAudience}). A different audience means the assertion was minted for another server and must be rejected.`,
      citation: CITATIONS.aud,
      actual: p.aud,
    });
  } else {
    verdicts.push({
      id: "aud",
      claim: "aud",
      status: "pass",
      detail:
        "Audience names the authorization server the assertion is redeemed at.",
      citation: CITATIONS.aud,
      actual: p.aud,
    });
  }

  // resource
  if (!isNonEmptyString(p.resource)) {
    verdicts.push({
      id: "resource",
      claim: "resource",
      status: "warn",
      detail:
        "No resource indicator was included. It is optional in the ID-JAG draft, but including it can bind the resulting access token to a protected resource.",
      citation: CITATIONS.resource,
      actual: show(p.resource),
    });
  } else if (
    context.expectedResource &&
    p.resource !== context.expectedResource
  ) {
    verdicts.push({
      id: "resource",
      claim: "resource",
      status: "fail",
      detail: `Resource does not match the protected resource configured for this flow (${context.expectedResource}).`,
      citation: CITATIONS.resource,
      actual: p.resource,
    });
  } else {
    verdicts.push({
      id: "resource",
      claim: "resource",
      status: "pass",
      detail: "Resource identifies the protected resource being accessed.",
      citation: CITATIONS.resource,
      actual: p.resource,
    });
  }

  // client_id
  if (!isNonEmptyString(p.client_id)) {
    verdicts.push({
      id: "client_id",
      claim: "client_id",
      status: "fail",
      detail:
        "Missing client binding. The grant must name the client that will redeem it, so a different client can't replay it.",
      citation: CITATIONS.clientId,
      actual: show(p.client_id),
    });
  } else if (
    context.expectedClientId &&
    p.client_id !== context.expectedClientId
  ) {
    verdicts.push({
      id: "client_id",
      claim: "client_id",
      status: "fail",
      detail: `client_id does not match the client configured for this flow (${context.expectedClientId}). The authorization server must reject a grant bound to another client.`,
      citation: CITATIONS.clientId,
      actual: p.client_id,
    });
  } else {
    verdicts.push({
      id: "client_id",
      claim: "client_id",
      status: "pass",
      detail: "Grant is bound to the redeeming client.",
      citation: CITATIONS.clientId,
      actual: p.client_id,
    });
  }

  // jti
  verdicts.push(
    isNonEmptyString(p.jti)
      ? {
          id: "jti",
          claim: "jti",
          status: "pass",
          detail:
            "Unique token id present — the authorization server can detect replays of this assertion.",
          citation: CITATIONS.jti,
          actual: p.jti,
        }
      : {
          id: "jti",
          claim: "jti",
          status: "warn",
          detail:
            "No jti. Without a unique token id the authorization server can't detect a replayed assertion.",
          citation: CITATIONS.jti,
          actual: show(p.jti),
        }
  );

  // iat — REQUIRED by the ID-JAG draft; anchors the assertion's issue time.
  if (typeof p.iat !== "number" || !Number.isFinite(p.iat)) {
    verdicts.push({
      id: "iat",
      claim: "iat",
      status: "fail",
      detail:
        "Missing or non-numeric issued-at. The ID-JAG draft requires iat, so the authorization server can anchor the assertion's lifetime.",
      citation: CITATIONS.iat,
      actual: show(p.iat),
    });
  } else if (p.iat > now + IAT_SKEW_S) {
    verdicts.push({
      id: "iat",
      claim: "iat",
      status: "warn",
      detail:
        "Issued in the future. Check for clock skew between the identity provider and the authorization server.",
      citation: CITATIONS.iat,
      actual: formatEpochSeconds(p.iat),
    });
  } else {
    verdicts.push({
      id: "iat",
      claim: "iat",
      status: "pass",
      detail: "Issued-at present.",
      citation: CITATIONS.iat,
      actual: formatEpochSeconds(p.iat),
    });
  }

  // exp
  if (typeof p.exp !== "number" || !Number.isFinite(p.exp)) {
    verdicts.push({
      id: "exp",
      claim: "exp",
      status: "fail",
      detail:
        "Missing or non-numeric expiration. JWT authorization grants must carry an exp and be rejected once past it.",
      citation: CITATIONS.exp,
      actual: show(p.exp),
    });
  } else if (p.exp <= now) {
    verdicts.push({
      id: "exp",
      claim: "exp",
      status: "fail",
      detail:
        "Assertion is expired. An authorization server that still issues an access token for it is not validating exp.",
      citation: CITATIONS.exp,
      actual: formatEpochSeconds(p.exp),
    });
  } else if (p.exp - now > LONG_LIVED_THRESHOLD_S) {
    verdicts.push({
      id: "exp",
      claim: "exp",
      status: "warn",
      detail:
        "Unusually long lifetime for a single-use assertion. The ID-JAG is redeemed immediately, so a short exp (minutes) keeps the replay window small.",
      citation: CITATIONS.exp,
      actual: formatEpochSeconds(p.exp),
    });
  } else {
    verdicts.push({
      id: "exp",
      claim: "exp",
      status: "pass",
      detail: "Short-lived and not yet expired.",
      citation: CITATIONS.exp,
      actual: formatEpochSeconds(p.exp),
    });
  }

  // Subject resolution — the draft RECOMMENDS email and/or aud_sub so the
  // resource authorization server can map the grant onto one of its own
  // users. One combined row: either claim satisfies the recommendation.
  const hasEmail = isNonEmptyString(p.email);
  const hasAudSub = isNonEmptyString(p.aud_sub);
  verdicts.push(
    hasEmail || hasAudSub
      ? {
          id: "subject_resolution",
          claim: "email / aud_sub",
          status: "pass",
          detail:
            "A subject-resolution hint is present, so the authorization server can map the grant onto one of its own users.",
          citation: CITATIONS.subjectResolution,
          actual: [
            hasEmail ? `email: ${p.email}` : null,
            hasAudSub ? `aud_sub: ${p.aud_sub}` : null,
          ]
            .filter(Boolean)
            .join(", "),
        }
      : {
          id: "subject_resolution",
          claim: "email / aud_sub",
          status: "warn",
          detail:
            "Neither email nor aud_sub is present. The authorization server only has sub — an IdP-local identifier — to resolve the user, which breaks JIT provisioning and cross-system mapping.",
          citation: CITATIONS.subjectResolution,
          actual: "(absent)",
        }
  );

  // sub_id — the OPTIONAL structured subject identifier (draft §3.2.2) minted
  // for a SAML-resolving resource authorization server. Emitted ONLY when the
  // claim is present: absence is the norm for OIDC-side ID-JAGs and must not
  // add a row (or a warn/fail) to their lint summaries.
  if (p.sub_id !== undefined) {
    const record =
      p.sub_id && typeof p.sub_id === "object" && !Array.isArray(p.sub_id)
        ? (p.sub_id as Record<string, unknown>)
        : undefined;
    // draft §3.2.2: a saml-nameid subject id needs format "saml-nameid" with a
    // non-empty issuer and nameid; the optional qualifiers, when present, must
    // be strings. Anything else (null, a string, {}, wrong format, missing
    // nameid) cannot be resolved by a SAML AS and is a real defect, not a pass.
    const optionalStringOk = (value: unknown) =>
      value === undefined || isNonEmptyString(value);
    const valid =
      record !== undefined &&
      record.format === "saml-nameid" &&
      isNonEmptyString(record.issuer) &&
      isNonEmptyString(record.nameid) &&
      optionalStringOk(record.sp_name_qualifier) &&
      optionalStringOk(record.nameid_format);
    if (valid) {
      const summary = (
        [
          ["format", record!.format],
          ["issuer", record!.issuer],
          ["sp_name_qualifier", record!.sp_name_qualifier],
        ] as const
      )
        .filter(([, value]) => isNonEmptyString(value))
        .map(([key, value]) => `${key}: ${value}`)
        .join(", ");
      verdicts.push({
        id: "sub_id",
        claim: "sub_id",
        status: "pass",
        detail:
          "A well-formed saml-nameid subject identifier is present, so a SAML-resolving authorization server can map the user by NameID instead of the IdP-local sub.",
        citation: CITATIONS.subId,
        actual: summary,
      });
    } else {
      verdicts.push({
        id: "sub_id",
        claim: "sub_id",
        status: "fail",
        detail:
          'sub_id is present but malformed: it must be an object with format "saml-nameid" and non-empty issuer and nameid (draft §3.2.2). A SAML-resolving authorization server cannot map the user from this value.',
        citation: CITATIONS.subId,
        actual: show(p.sub_id),
      });
    }
  }

  return verdicts;
}
